import React, { useEffect, useState } from "react";
import { Modal, Select, Spin, message } from "antd";
import { userService } from "@iso/services";
import { isSuccess } from "@iso/lib/helpers/apiResponse";

const { Option } = Select;

/**
 * Modal for assigning a property user as the responsible/assignee for a
 * single asset. Mirrors the SDS variant — kept separate so asset-side
 * copy / validation can evolve independently (e.g. "Asset" vs "File").
 *
 * The assignee is the user the expiry-reminder cron notifies. Pass
 * `initialUserId` so the picker preselects the current assignee on edit.
 */
const AssetAssignUserModal = ({
  visible,
  propertyId,
  initialUserId = null,
  onCancel,
  onConfirm,
  confirmLoading = false,
  assetName = "",
}) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(initialUserId || undefined);

  useEffect(() => {
    if (!visible) return;
    setSelectedId(initialUserId || undefined);

    if (!propertyId) {
      setUsers([]);
      return;
    }
    setLoading(true);
    userService
      .getUserForProperty(propertyId)
      .then((res) => {
        // Verify success before reading the array so a failure response
        // does not silently render an empty list as if there were no
        // users.
        if (isSuccess(res) && Array.isArray(res.users)) {
          setUsers(res.users);
        } else {
          setUsers([]);
        }
      })
      .catch((err) => {
        console.error("getUserForProperty failed", err);
        message.error("Failed to load property users");
        setUsers([]);
      })
      .finally(() => setLoading(false));
  }, [visible, propertyId, initialUserId]);

  const handleOk = () => {
    if (!selectedId) {
      // Unassign isn't supported via this modal — match the SDS/file-
      // manager pattern. Clearing the assignee should be a deliberate
      // backend call if ever wired up.
      return;
    }
    onConfirm(selectedId);
  };

  const formatLabel = (user) => {
    const name = `${user.name || ""} ${user.surname || ""}`.trim();
    return name || user.email || `User #${user.id}`;
  };

  return (
    <Modal
      title={
        assetName ? `Assign user to "${assetName}"` : "Assign user to asset"
      }
      visible={visible}
      onOk={handleOk}
      onCancel={onCancel}
      okText="Assign"
      okButtonProps={{ disabled: !selectedId }}
      confirmLoading={confirmLoading}
      destroyOnClose
      zIndex={1200}
      wrapClassName="asset-assign-user-modal"
    >
      <Spin spinning={loading}>
        <Select
          showSearch
          style={{ width: "100%" }}
          placeholder="Select a property user"
          value={selectedId}
          onChange={(value) => setSelectedId(value)}
          dropdownStyle={{ zIndex: 1300 }}
          filterOption={(input, option) =>
            option?.children?.toString().toLowerCase().includes(input.toLowerCase())
          }
        >
          {users.map((user) => (
            <Option key={user.id} value={user.id}>
              {formatLabel(user)}
            </Option>
          ))}
        </Select>
      </Spin>
      <div style={{ marginTop: 12, fontSize: 12, color: "#888" }}>
        Pick a property user — they will be notified about this asset's
        expiry on the configured reminder schedule (30 days before, 7 days
        before, on the day, and 7 days after).
      </div>
    </Modal>
  );
};

export default AssetAssignUserModal;
